import { ServiceType } from "@/data/model/Enums";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";
import { Image } from "@/shared/ui/Image";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { trans } from "@/app/localization/Trans";
import { CovidSignupPopover } from "@/app/popover/CovidSignupPopover";
import { TableSelectionPopover } from "@/app/popover/TableSelectionPopover";
import { CoronaSignupState } from "@/app/services/userCovidCerificateService";
import { UserCovidCertificateService } from "@/app/services/userCovidCerificateService";
import { Helper } from "@/app/ui/Helper";
import { ServicesIcon } from "@/app/ui/ServicesIcon";
import { UserMenu } from "@/app/ui/UserMenu";

export class CovidController extends BaseController {
    private $head: JQuery;
    private $title: JQuery;
    public userMenu: UserMenu;
    private popover?: CovidSignupPopover;
    public onShowServices?: { () };
    userCovidCertificateService: UserCovidCertificateService;

    constructor() {
        super("covid");

        this.userCovidCertificateService = UserCovidCertificateService.getInstance();

        this.$head = $('<div class="head"></div>').appendTo(this.$view);
        this.$title = $('<div class="title">' + trans.covid.title + "</div>").appendTo(this.$head);

        new ServicesIcon().appendTo(this.$head).onClick = () => {
            if (this.onShowServices !== undefined) this.onShowServices();
        };

        this.userMenu = new UserMenu().appendTo(this.$head);
    }

    protected onBecomeVisible() {
        const serviceDesignation = Helper.getCurrentServiceDesignation();
        this.$title.text(serviceDesignation === undefined ? trans.covid.title : serviceDesignation);

        this.checkState();
        TastierClient.context.onAllocationChange.add(this.checkState, this);
        TastierClient.context.onGuestNumberChanged.add(this.refreshGuestNumber, this);
        TastierClient.identityContext.onIdentityChange.add(this.checkState, this);
    }

    protected onBecomeInvisible() {
        // Make sure the popover will disappear along with controller
        this.popover?.hide();
        this.popover = undefined;

        TastierClient.context.onAllocationChange.remove(this.checkState, this);
        TastierClient.context.onGuestNumberChanged.remove(this.refreshGuestNumber, this);
        TastierClient.identityContext.onIdentityChange.remove(this.checkState, this);
    }

    private setValidating() {
        this.$view.children(":not('.head')").remove();

        const validating = $('<div class="validating" />').appendTo(this.$view);
        validating.html(
            '<div class="loading"><div class="loader"></div>' + trans.covid.validatingCertificate + "</div>"
        );
    }

    private setPending() {
        this.$view.children(":not('.head')").remove();
        const pending = $('<div class="pending scrollable" />').appendTo(this.$view);

        const co = $('<div class="content" />').appendTo(pending);
        co.append('<div class="name">' + TastierClient.context.currentAllocation!.locationName + "</div>");
        co.append('<div class="welcome">' + trans.covid.welcome + "</div>");

        const bottom = $('<div class="bottom" />').appendTo(this.$view);
        Ripple.bind(
            $('<button class="btn">' + trans.covid.letsgo + "</button>")
                .appendTo(bottom)
                .click((e) => {
                    e.preventDefault();
                    this.openCovidPopovers(false);
                })
        );

        this.openCovidPopovers(true);
    }

    private setValid() {
        this.$view.children(":not('.head')").remove();

        const valid = $('<div class="valid scrollable" />').appendTo(this.$view);
        const co = $('<div class="content" />').appendTo(valid);

        const tableInfo = $('<div class="tableinfo" />').appendTo(co);
        const icon = $('<div class="icon"><i class="fal fa-check"></i></div>').appendTo(tableInfo);
        const table = $('<div class="table" />').appendTo(tableInfo);
        table.append(
            '<div class="name">' +
                Format.htmlEscape(TastierClient.context.currentAllocation!.locationName) +
                ", " +
                Format.htmlEscape(TastierClient.context.currentAllocation!.tableName) +
                "</div>"
        );
        table.append(
            '<div class="time">' +
                DateTime.toDateString(TastierClient.context.currentAllocation!.allocatedSince) +
                ", " +
                DateTime.toTimeString(TastierClient.context.currentAllocation!.allocatedSince) +
                "</div>"
        );

        const personInfo = $('<div class="personinfo" />').appendTo(co);
        const profile = TastierClient.identityContext.getProfile()!;

        if (profile.lastName !== undefined)
            personInfo.append(
                '<div class="fact"><div class="val">' +
                    Format.htmlEscape(profile.lastName) +
                    '</div><div class="lbl">' +
                    trans.covidSignupPopover.lastName +
                    "</div></div>"
            );

        if (profile.firstName !== undefined)
            personInfo.append(
                '<div class="fact"><div class="val">' +
                    Format.htmlEscape(profile.firstName) +
                    '</div><div class="lbl">' +
                    trans.covidSignupPopover.firstName +
                    "</div></div>"
            );

        if (profile.birthday !== undefined)
            personInfo.append(
                '<div class="fact"><div class="val">' +
                    DateTime.toDateString(profile.birthday) +
                    '</div><div class="lbl">' +
                    trans.covidSignupPopover.birthday +
                    "</div></div>"
            );

        if (Helper.getCovidServiceConfigValue<boolean>("showGuestNumber") === true)
            personInfo.append(
                '<div class="fact guestnumber"><div class="val">' +
                    (TastierClient.context.currentAllocation!.guestNumber === 0
                        ? "-"
                        : TastierClient.context.currentAllocation!.guestNumber + "") +
                    '</div><div class="lbl">' +
                    trans.covid.guestNumber +
                    "</div></div>"
            );

        if (profile.identityCard !== undefined) {
            const idDocBtn = $('<div class="iddocbtn"></div>').appendTo(co);
            idDocBtn.append('<div class="thumb"><img src="' + profile.identityCard + '" /></div>');
            idDocBtn.append('<div class="openicon"><i class="fas fa-expand-alt"></i></div>');

            Ripple.bind(
                idDocBtn.click(() => {
                    this.openIdentityDocumentInFullscreen();
                })
            );
        }

        const bottom = $('<div class="bottom" />').appendTo(this.$view);
        const leaveBtn = Ripple.bind(
            $('<button class="btn submit">' + trans.covid.checkout + "</button>")
                .appendTo(bottom)
                .click(() => {
                    leaveBtn.addClass("loading disabled ").prop("disabled", true);

                    TastierClient.context.deallocate().catch((ex) => {
                        logError(ex);
                        leaveBtn.removeClass("loading disabled ").prop("disabled", false);
                    });
                })
        );

        bottom.append('<div class="hint">' + trans.covid.clickBeforeYouGo + "</div>");

        icon.delay(300).queue((a) => {
            icon.addClass("circlein");
            a();
        });
    }

    private checkState(isLogout?: boolean) {
        setTimeout(
            async () => {
                if (TastierClient.context.currentAllocation === undefined) return;

                this.setValidating();
                const state = await this.userCovidCertificateService.isCovidValid(Helper.getCovidServiceConfig()!);
                if (state === CoronaSignupState.Valid) this.setValid();
                else this.setPending();
            },
            isLogout !== undefined && isLogout === true ? 600 : 1
        );
    }

    private refreshGuestNumber() {
        if (Helper.getCovidServiceConfigValue<boolean>("showGuestNumber") === false) return;

        const validC = this.$view.find("> .valid > .content");
        const gn = validC.find("> .guestnumber > .val");
        if (gn.length > 0)
            gn.html(
                TastierClient.context.currentAllocation!.guestNumber === 0
                    ? "-"
                    : TastierClient.context.currentAllocation!.guestNumber + ""
            );
        else
            validC.append(
                '<div class="fact guestnumber"><div class="val">' +
                    (TastierClient.context.currentAllocation!.guestNumber === 0
                        ? "-"
                        : TastierClient.context.currentAllocation!.guestNumber + "") +
                    '</div><div class="lbl">' +
                    trans.covid.guestNumber +
                    "</div></div>"
            );
    }

    private openCovidPopovers(delayed: boolean) {
        if (!Helper.hasService(ServiceType.Covid)) return;

        setTimeout(
            () => {
                if (TableSelectionPopover.isAlreadyOpen()) return;

                if (!CovidSignupPopover.isAlreadyOpen()) {
                    this.popover = new CovidSignupPopover();
                    this.popover.onSigninClicked = () => this.userMenu.showLogin();
                    this.popover.onSignUpClicked = () => this.userMenu.showSignup();
                    this.popover.show();
                }
            },
            delayed ? 500 : 1
        );
    }

    private async openIdentityDocumentInFullscreen() {
        const profile = TastierClient.identityContext.getProfile()!;
        if (profile.identityCard === undefined) return;

        const parent = $(".app");

        const dim = $('<div class="dim" />').appendTo(parent);

        dim[0].offsetHeight;
        dim.addClass("shown");

        const pop = $('<div class="identitydocmodal"></div>').appendTo(parent);
        const bigUrl = profile.identityCard.replace("identitycardthumb", "identitycard");

        const hide = () => {
            dim.removeClass("shown");
            pop.removeClass("shown")
                .delay(400)
                .queue((e) => {
                    dim.remove();
                    pop.remove();

                    e();
                });
        };

        dim.click(() => {
            hide();
        });

        pop.click(() => {
            hide();
        });

        const s = await Image.preloadImage(bigUrl);
        let img: JQuery;
        if (s !== undefined) img = $(s).appendTo(pop);
        else img = $('<img src="' + profile.identityCard + '" />').appendTo(pop);

        pop[0].offsetHeight;

        const imgElement = img[0] as HTMLImageElement;
        const imgHeight = (parent.width() / imgElement.width) * imgElement.height;
        pop.css({
            height: imgHeight,
            "margin-top": imgHeight / -2,
        });

        pop.addClass("shown");
        pop.append('<div class="close">' + trans.general.close + "</div>");
    }
}
